import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from "react";
import SpinningLoader from "../components/common/SpinningLoader";
import { usePortfolioEstimateAssets } from "../hooks/query/queries";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { useWalletScope, type WalletQueryScope } from "./WalletScopeContext";

export interface PortfolioAsset {
  assetId?: string;
  asset?: string;
  currencyType?: string;
  type?: string;
  [key: string]: unknown;
}

export interface PortfolioData {
  assets?: PortfolioAsset[];
  zarEstimate?: number;
  usdtEstimate?: number;
  portfolioEstimate?: string;
  portfolioEstimateRaw?: number;
  timestamp?: number | string;
  [key: string]: unknown;
}

interface PortfolioEstimateContextValue {
  portfolioData: PortfolioData | undefined;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
  fetchPortfolioEstimate: (
    forceRefresh?: boolean
  ) => Promise<PortfolioData | undefined>;
  clearPortfolioData: () => void;
  getAssetsByType: (type: string) => PortfolioAsset[];
  getAssetById: (assetId: string) => PortfolioAsset | null;
}

const PortfolioEstimateContext = createContext<
  PortfolioEstimateContextValue | undefined
>(undefined);

interface PortfolioEstimateProviderProps {
  children: ReactNode;
  scope?: WalletQueryScope;
  showInitialLoader?: boolean;
}

export const PortfolioEstimateProvider: React.FC<
  PortfolioEstimateProviderProps
> = ({ children, scope: scopeOverride, showInitialLoader = true }) => {
  const { isAuthenticated, userProfile } = useAuth();
  const canLoadPortfolioEstimate =
    isAuthenticated &&
    userProfile?.emailVerified === true &&
    userProfile?.profile_completed === true;
  const queryClient = useQueryClient();
  const contextScope = useWalletScope();
  const walletScope = scopeOverride ?? contextScope;
  const scopedRequest = walletScope.businessId ? walletScope : undefined;

  const {
    data: portfolioData,
    isLoading,
    error: queryError,
    dataUpdatedAt: lastFetched,
  } = usePortfolioEstimateAssets(scopedRequest);

  const fetchPortfolioEstimate = useCallback(
    async (
      forceRefresh: boolean = false
    ): Promise<PortfolioData | undefined> => {
      if (!canLoadPortfolioEstimate) {
        return undefined;
      }

      if (forceRefresh) {
        await queryClient.invalidateQueries({
          queryKey: ["portfolioEstimateAssets"],
        });
      }
      const data = queryClient.getQueryData<PortfolioData>([
        "portfolioEstimateAssets",
      ]);
      return (
        data || (portfolioData as PortfolioData | null | undefined) || undefined
      );
    },
    [canLoadPortfolioEstimate, queryClient, portfolioData]
  );

  const clearPortfolioData = useCallback((): void => {
    queryClient.removeQueries({
      queryKey: ["portfolioEstimateAssets"],
    });
  }, [queryClient]);

  const getAssetsByType = useCallback(
    (type: string): PortfolioAsset[] => {
      if (!portfolioData?.assets) return [];
      return portfolioData.assets.filter(
        (asset: PortfolioAsset) =>
          asset.currencyType === type || asset.type === type
      );
    },
    [portfolioData]
  );

  const getAssetById = useCallback(
    (assetId: string): PortfolioAsset | null => {
      if (!portfolioData?.assets) return null;
      return (
        portfolioData.assets.find(
          (asset: PortfolioAsset) =>
            asset.assetId === assetId || asset.asset === assetId
        ) || null
      );
    },
    [portfolioData]
  );

  useEffect(() => {
    if (!canLoadPortfolioEstimate) {
      clearPortfolioData();
    }
  }, [canLoadPortfolioEstimate, clearPortfolioData]);

  const error =
    (queryError as Error | null)?.message ||
    (queryError ? String(queryError) : null);

  const value = useMemo(
    (): PortfolioEstimateContextValue => ({
      portfolioData: portfolioData as PortfolioData | undefined,
      isLoading,
      error,
      lastFetched: lastFetched || null,
      fetchPortfolioEstimate,
      clearPortfolioData,
      getAssetsByType,
      getAssetById,
    }),
    [
      portfolioData,
      isLoading,
      error,
      lastFetched,
      fetchPortfolioEstimate,
      clearPortfolioData,
      getAssetsByType,
      getAssetById,
    ]
  );

  if (
    showInitialLoader &&
    canLoadPortfolioEstimate &&
    isLoading &&
    !portfolioData
  ) {
    return <SpinningLoader />;
  }

  return (
    <PortfolioEstimateContext.Provider value={value}>
      {children}
    </PortfolioEstimateContext.Provider>
  );
};

export const usePortfolioEstimate = (): PortfolioEstimateContextValue => {
  const context = useContext(PortfolioEstimateContext);
  if (!context) {
    throw new Error(
      "usePortfolioEstimate must be used within PortfolioEstimateProvider"
    );
  }
  return context;
};
